import { Injectable } from '@angular/core';
import createAuth0Client, { Auth0Client, Auth0ClientOptions, RedirectLoginOptions, LogoutOptions, GetTokenSilentlyOptions } from '@auth0/auth0-spa-js';
import { PortalBootstrapService } from './portal-bootstrap.service';
import { TeamService } from './team.service';

/**
 * Thin Angular wrapper around @auth0/auth0-spa-js — instantiates the underlying
 * Auth0Client lazily using whatever Auth0 config the portal advertises through
 * COM_PortalSettings (loaded by PortalBootstrapService at app startup).
 *
 * Portals that don't have Auth0 enabled never instantiate the client.
 */
@Injectable({ providedIn: 'root' })
export class Auth0ClientService {
	private clientPromise: Promise<Auth0Client> | null = null;

	constructor(
		private bootstrap: PortalBootstrapService,
		private teamService: TeamService
	) {}

	public get isEnabledForPortal(): boolean {
		return this.bootstrap.auth0Enabled
			&& !!this.bootstrap.auth0Config.domain
			&& !!this.bootstrap.auth0Config.clientId;
	}

	public getClient(): Promise<Auth0Client> {
		if (!this.isEnabledForPortal) {
			return Promise.reject(new Error('Auth0 is not enabled for the current portal.'));
		}

		if (!this.clientPromise) {
			const teamCode = this.getTeamCode();
			const options: Auth0ClientOptions = {
				domain: this.bootstrap.auth0Config.domain,
				client_id: this.bootstrap.auth0Config.clientId,
				audience: this.bootstrap.auth0Config.audience,
				redirect_uri: `${window.location.origin}/${teamCode}/auth0-callback`,
				cacheLocation: 'localstorage',
				useRefreshTokens: true
			};
			this.clientPromise = createAuth0Client(options);
		}

		return this.clientPromise;
	}

	public async loginWithRedirect(returnTo?: string, signup?: boolean): Promise<void> {
		const client = await this.getClient();
		const options: RedirectLoginOptions = {};
		if (returnTo) {
			(options as any).appState = { returnTo };
		}
		if (signup) {
			// auth0-spa-js v1.x: screen_hint is a top-level RedirectLoginOptions property
			// (BaseLoginOptions). In v2.x it moves under authorizationParams — update if upgraded.
			(options as any).screen_hint = 'signup';
		}
		await client.loginWithRedirect(options);
	}

	public async handleRedirectCallback(): Promise<{ returnTo?: string } | null> {
		const client = await this.getClient();
		const result = await client.handleRedirectCallback();
		return (result && result.appState) ? result.appState : null;
	}

	public async isAuthenticated(): Promise<boolean> {
		if (!this.isEnabledForPortal) {
			return false;
		}
		try {
			const client = await this.getClient();
			return client.isAuthenticated();
		} catch {
			return false;
		}
	}

	public async getAccessTokenSilently(options?: GetTokenSilentlyOptions): Promise<string> {
		const client = await this.getClient();
		return client.getTokenSilently(options);
	}

	public async logout(returnToPath?: string): Promise<void> {
		const client = await this.getClient();
		const teamCode = this.getTeamCode();
		const fallback = `${window.location.origin}/${teamCode}/login`;
		const options: LogoutOptions = {
			returnTo: returnToPath || fallback
		};
		client.logout(options);
	}

	// Resolves the portal/team code for building redirect URLs. Prefers the resolved team
	// template, but falls back to the first URL path segment (e.g. /omonoia/... -> 'omonoia')
	// so the Auth0 redirect_uri always includes the portal even if the template isn't set yet.
	private getTeamCode(): string {
		if (this.teamService.teamTemplate && this.teamService.teamTemplate.teamId) {
			return this.teamService.teamTemplate.teamId;
		}
		const segments = window.location.pathname.replace(/^\//, '').split('/');
		return segments[0] || '';
	}
}
